import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { RealPropertyModel } from '../../../../../../../typings/api/skymap/rest/v1/project';
import { SkyMapAxiosServiceFactory } from '../../../../../services/axios/skymap-axios-service-factory';
import { ComponentStatus, useComponentStatus } from '../../../../hooks/use-component-status';
import { useNonce } from '../../../../hooks/use-nonce';
import { useProjectSnapshot } from '../../../../hooks/use-project-snapshot';
import { useUserState } from '../../../../state/user-state/user-state';
import { Button } from '../../../button/button';
import { InfoBox } from '../../../info-box/info-box';
import { OverlayLoader } from '../../../overlay-loader/overlay-loader';
import { Stack } from '../../../stack/stack';
import { RealPropertyNoDesignLicensePanel } from './panels/real-property-no-design-license-panel';
import { PurchaseHistorySection } from './purchase-history-section/purchase-history-section';
import { SearchRealPropertiesSection } from './search-section/search-real-properties-section';

export const RealPropertyMenu = () => {
  const userState = useUserState();
  const hasDesignLicense = userState.hasDesignToolAccessForCurrentProject() ?? false;
  const { setStatus, hasError, isLoading } = useComponentStatus('loading');
  const { properties, addRealProperty, retry } = useProjectProperties(setStatus, hasDesignLicense);

  if (!hasDesignLicense) {
    return <RealPropertyNoDesignLicensePanel />;
  }

  if (hasError) {
    return <FailedLoadingRealProperties retryCallback={retry} />;
  }

  return (
    <OverlayLoader visible={isLoading}>
      <SearchRealPropertiesSection addRealProperty={addRealProperty} properties={properties} />
      <PurchaseHistorySection items={properties} />
    </OverlayLoader>
  );
};

const useProjectProperties = (setState: (state: ComponentStatus) => void, enabled: boolean) => {
  const { projectId } = useProjectSnapshot();
  const [properties, setProperties] = useState<RealPropertyModel[]>([]);
  const [nonce, setNonce] = useNonce();

  const fetchProjectProperties = useCallback(async () => {
    if (!projectId) {
      return;
    }
    try {
      setState('loading');

      const response = await SkyMapAxiosServiceFactory.instance
        .createProjectServiceV1()
        .getProjectRealProperties({
          path: {
            projectId,
          },
        });

      setProperties(response.data);
      setState('success');
    } catch {
      setProperties([]);
      setState('error');
    }
  }, [projectId, setState]);

  const addRealProperty = useCallback(
    (property: RealPropertyModel) => {
      setProperties([...properties, property]);
    },
    [properties],
  );

  useEffect(() => {
    if (enabled) {
      void fetchProjectProperties();
    }
  }, [fetchProjectProperties, nonce, enabled]);

  return { properties, addRealProperty, retry: setNonce };
};

const FailedLoadingRealProperties = (props: { retryCallback: () => void }) => {
  const { t } = useTranslation();

  return (
    <Margin>
      <InfoBox color="red">
        <Stack spacing={0.5}>
          {t('onDemand.error', { ns: 'components' })}

          <Button color="primary" variant="contained" onClick={() => props.retryCallback()}>
            {t('retry', { ns: 'common' })}
          </Button>
        </Stack>
      </InfoBox>
    </Margin>
  );
};

const Margin = styled.div`
  margin: 0.4em;
`;
